import { DeliveryCompany } from "../../../../entity/DeliveryCompany";
import {
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  HStack,
  Drawer,
  Input,
  Switch,
  VStack,
  Text,
  Button,
  Select,
  IconButton,
  Grid,
  Box,
  Badge,
  Collapse,
  Flex,
  Divider,
  useColorModeValue,
  Tooltip,
  Circle,
  FormHelperText,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DeliveryFragment } from "../../../../entity/DeliveryFragment";
import { IoMdAdd, IoMdCheckmark, IoMdClose } from "react-icons/io";
import { WSDeliveryInstance } from "../../../../services/remote/delivery/WSDelivery";
import { Fragments } from "./Fragments";
import "./CreateUpdateDelivery.css";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { WSMedia } from "../../../../services/remote/media/WSMedia";
import {
  FiUpload,
  FiX,
  FiImage,
  FiPlus,
  FiFolder,
  FiDatabase,
  FiSettings,
  FiShield,
  FiKey,
  FiLock,
  FiTag,
} from "react-icons/fi";

interface CreateUpdateDeliveryCompanyProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (company: DeliveryCompany) => void;
  company: DeliveryCompany | null;
}

export const CreateUpdateDeliveryCompany: React.FC<
  CreateUpdateDeliveryCompanyProps
> = ({
  isOpen,
  onClose,
  onSave,
  company,
}: CreateUpdateDeliveryCompanyProps) => {
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [logoUploadError, setLogoUploadError] = useState<string | null>(null);
  const [fragments, setFragments] = useState<DeliveryFragment[]>([]);
  const [fragment, setFragment] = useState<DeliveryFragment>({
    label: "",
    key: "",
    type: "text",
    defaultValue: "",
    isRequired: false,
  });
  const [onAdding, setOnAdding] = useState(false);
  const [showFragments, setShowFragments] = useState(false);

  const handleClose = () => {
    onClose();
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setLogoUploadError(
        t("deliveryConfig.logoTooLarge") || "Logo size should be less than 5MB",
      );
      return;
    }

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setLogoUploadError(
        t("deliveryConfig.invalidFileType") || "Please upload an image file",
      );
      return;
    }

    setLogoUploadError(null);
    setIsUploadingLogo(true);

    try {
      const media = WSMedia.getInstance();
      const presignedUrl = await media.getGeneratedURL();
      const finalUrl = await media.saveImage(presignedUrl, file, true);

      setNewCompany((prev) => ({ ...prev, logo: finalUrl }));
    } catch (error) {
      console.error("Logo upload failed:", error);
      setLogoUploadError(
        t("deliveryConfig.logoUploadFailed") || "Failed to upload logo",
      );
    } finally {
      setIsUploadingLogo(false);
      e.target.value = "";
    }
  };

  const handleLogoRemove = () => {
    setNewCompany((prev) => ({ ...prev, logo: "" }));
    setLogoUploadError(null);
  };

  const { t } = useTranslation();
  const [selectedType, setSelectedType] = useState<string | undefined>(
    undefined,
  );

  const [newCompany, setNewCompany] = useState<DeliveryCompany>({
    name: "",
    logo: "",
    isActive: false,
    fragments: [],
    baseUrl: "",
    apiKeyIsRequired: true,
    apiKeyLabel: "",
    apiSecretIsRequired: false,
    apiSecretLabel: "",
  });

  // Color mode values for better theming
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const hoverBg = useColorModeValue("gray.50", "gray.700");
  const cardBg = useColorModeValue("white", "gray.800");
  const sectionBg = useColorModeValue("gray.50", "gray.700");

  useEffect(() => {
    if (company) {
      setNewCompany({
        id: company.id,
        name: company.name,
        logo: company.logo,
        isActive: company.isActive,
        fragments: company.fragments || [],
        baseUrl: company.baseUrl || "",
        apiKeyLabel: company.apiKeyLabel || "",
        apiKeyIsRequired: company.apiKeyIsRequired || true,
        apiSecretLabel: company.apiSecretLabel || "",
        apiSecretIsRequired: company.apiSecretIsRequired || false,
      });
      setFragments(company.fragments || []);
    } else {
      setNewCompany({
        name: "",
        logo: "",
        isActive: false,
        fragments: [],
        baseUrl: "",
        apiKeyLabel: "",
        apiKeyIsRequired: true,
        apiSecretLabel: "",
        apiSecretIsRequired: false,
      });
      setFragments([]);
    }
    setOnAdding(false);
  }, [company, isOpen]);

  const handleAddingNewFragment = () => {
    setFragments([...fragments, fragment]);
    setOnAdding(false);
    setSelectedType(undefined);
  };

  const handleSave = () => {
    const companyToSave = {
      ...newCompany,
      fragments: fragments,
    };

    if (company?.id) {
      onSave({
        ...companyToSave,
        id: company.id,
      });
    } else {
      onSave(companyToSave);
    }
  };

  const handleDeleteFragment = async (index: number) => {
    setFragments(fragments.filter((_, i) => i !== index));
    await WSDeliveryInstance.deleteFragmentById(fragments[index].id!!);
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="full">
      <DrawerOverlay backdropFilter="blur(4px)" />
      <DrawerContent>
        <DrawerCloseButton size="lg" />
        <DrawerHeader borderBottomWidth="1px" borderColor={borderColor}>
          <Flex justify="space-between" align="center" w="full">
            <VStack align="start" spacing={1}>
              <Text fontSize="2xl" fontWeight="bold">
                {company
                  ? t("delivery.editDeliveryCompany") || "Edit Delivery Company"
                  : t("delivery.addDeliveryCompany") || "Add Delivery Company"}
              </Text>
              <Text fontSize="sm" color="gray.500">
                {company
                  ? t("deliveryConfig.editCompanyDetails") ||
                    "Update company configuration"
                  : t("deliveryConfig.createNewCompany") ||
                    "Create a new delivery company"}
              </Text>
            </VStack>
            <Button
              onClick={handleSave}
              colorScheme="blue"
              size="lg"
              px={8}
              _hover={{
                transform: "translateY(-2px)",
                boxShadow: "lg",
              }}
              transition="all 0.2s"
            >
              {company
                ? t("delivery.update") || "Update"
                : t("delivery.create") || "Create"}
            </Button>
          </Flex>
        </DrawerHeader>

        <DrawerBody bg={useColorModeValue("gray.50", "gray.900")}>
          <VStack spacing={8} align="stretch" maxW="1200px" mx="auto" py={4}>
            {/* Basic Information Section */}
            <Box
              bg={cardBg}
              p={6}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
            >
              <Flex align="center" mb={6}>
                <FiFolder size={20} style={{ marginRight: "12px" }} />
                <Text fontSize="lg" fontWeight="semibold">
                  {t("deliveryConfig.basicInformation") || "Basic Information"}
                </Text>
                <Divider flex={1} ml={4} />
              </Flex>

              <Grid templateColumns="1fr 300px" gap={6}>
                <FormControl>
                  <FormLabel fontWeight="medium" fontSize="sm">
                    {t("deliveryConfig.companyName")}
                  </FormLabel>
                  <Input
                    value={newCompany.name}
                    onChange={(e) =>
                      setNewCompany({ ...newCompany, name: e.target.value })
                    }
                    placeholder={t("deliveryConfig.enterCompanyName")}
                    size="lg"
                    _hover={{ borderColor: "blue.400" }}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px #3182ce",
                    }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontWeight="medium" fontSize="sm">
                    {t("logo") || "Logo"}
                  </FormLabel>

                  {newCompany.logo ? (
                    <Flex
                      h="12"
                      gap={3}
                      align="center"
                      borderWidth="2px"
                      borderColor="blue.200"
                      borderRadius="lg"
                      px={2}
                      bg="white"
                    >
                      <Box
                        boxSize="8"
                        borderRadius="md"
                        overflow="hidden"
                        flexShrink={0}
                        bg="gray.50"
                      >
                        <img
                          src={newCompany.logo}
                          alt="Company logo"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "contain",
                          }}
                        />
                      </Box>
                      <Text
                        fontSize="xs"
                        color="green.600"
                        fontWeight="medium"
                        flex="1"
                        noOfLines={1}
                      >
                        {t("deliveryConfig.uploaded") || "Uploaded"}
                      </Text>
                      <Button
                        as="label"
                        size="sm"
                        leftIcon={<FiUpload />}
                        isLoading={isUploadingLogo}
                        cursor="pointer"
                        colorScheme="blue"
                        variant="ghost"
                      >
                        {t("deliveryConfig.replaceLogo") || "Replace"}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleLogoUpload}
                        />
                      </Button>
                      <IconButton
                        aria-label="Remove logo"
                        icon={<FiX />}
                        size="sm"
                        variant="ghost"
                        onClick={handleLogoRemove}
                        colorScheme="red"
                      />
                    </Flex>
                  ) : (
                    <Button
                      as="label"
                      h="12"
                      w="full"
                      justifyContent="flex-start"
                      fontWeight="normal"
                      borderWidth="2px"
                      borderStyle="dashed"
                      borderColor={logoUploadError ? "red.300" : "gray.300"}
                      borderRadius="lg"
                      bg={hoverBg}
                      cursor="pointer"
                      colorScheme="blue"
                      variant="ghost"
                      leftIcon={
                        isUploadingLogo ? undefined : <FiImage size={18} />
                      }
                      isLoading={isUploadingLogo}
                      loadingText={
                        t("deliveryConfig.uploading") || "Uploading..."
                      }
                      _hover={{ borderColor: "blue.400", bg: "blue.50" }}
                    >
                      <Text fontSize="sm" color="gray.500">
                        {t("deliveryConfig.uploadLogo") ||
                          "Click to upload logo"}
                      </Text>
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={handleLogoUpload}
                      />
                    </Button>
                  )}

                  <FormHelperText
                    fontSize="xs"
                    color={logoUploadError ? "red.500" : "gray.400"}
                  >
                    {logoUploadError ||
                      t("deliveryConfig.logoRequirements") ||
                      "PNG, JPG or SVG • Max 5MB"}
                  </FormHelperText>
                </FormControl>
              </Grid>
            </Box>

            {/* Configuration Section */}
            <Box
              bg={cardBg}
              p={6}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
            >
              <Flex align="center" mb={6}>
                <FiSettings size={20} style={{ marginRight: "12px" }} />
                <Text fontSize="lg" fontWeight="semibold">
                  {t("deliveryConfig.configuration") || "Configuration"}
                </Text>
                <Divider flex={1} ml={4} />
              </Flex>

              <VStack spacing={6} align="stretch">
                <Grid templateColumns="1fr 1fr" gap={6}>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">
                      {t("deliveryConfig.baseUrl")}
                    </FormLabel>
                    <Input
                      value={newCompany.baseUrl}
                      onChange={(e) =>
                        setNewCompany({
                          ...newCompany,
                          baseUrl: e.target.value,
                        })
                      }
                      placeholder="https://api.example.com"
                      size="lg"
                      _hover={{ borderColor: "blue.400" }}
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182ce",
                      }}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">
                      {t("deliveryConfig.isActive")}
                    </FormLabel>
                    <Flex align="center" h="48px">
                      <Switch
                        isChecked={newCompany.isActive}
                        onChange={(e) =>
                          setNewCompany({
                            ...newCompany,
                            isActive: e.target.checked,
                          })
                        }
                        size="lg"
                        colorScheme="green"
                      />
                      <Text ml={3} fontSize="sm" color="gray.600">
                        {newCompany.isActive
                          ? t("deliveryConfig.active") || "Active"
                          : t("deliveryConfig.inactive") || "Inactive"}
                      </Text>
                    </Flex>
                  </FormControl>
                </Grid>
              </VStack>
            </Box>

            {/* API Configuration Section */}
            <Box
              bg={cardBg}
              p={6}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
            >
              <Flex align="center" mb={6}>
                <FiShield size={20} style={{ marginRight: "12px" }} />
                <Text fontSize="lg" fontWeight="semibold">
                  {t("deliveryConfig.apiConfiguration") || "API Configuration"}
                </Text>
                <Divider flex={1} ml={4} />
              </Flex>

              <VStack spacing={6} align="stretch">
                <Grid templateColumns="1fr 1fr" gap={6}>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">
                      {t("deliveryConfig.apiKeyIsRequired")}
                    </FormLabel>
                    <Flex align="center" h="48px">
                      <Switch
                        isChecked={newCompany.apiKeyIsRequired}
                        onChange={(e) =>
                          setNewCompany({
                            ...newCompany,
                            apiKeyIsRequired: e.target.checked,
                          })
                        }
                        size="lg"
                        colorScheme="blue"
                      />
                      <Text ml={3} fontSize="sm" color="gray.600">
                        {newCompany.apiKeyIsRequired ? "Required" : "Optional"}
                      </Text>
                    </Flex>
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">
                      {t("deliveryConfig.apiSecretIsRequired")}
                    </FormLabel>
                    <Flex align="center" h="48px">
                      <Switch
                        isChecked={newCompany.apiSecretIsRequired}
                        onChange={(e) =>
                          setNewCompany({
                            ...newCompany,
                            apiSecretIsRequired: e.target.checked,
                          })
                        }
                        size="lg"
                        colorScheme="purple"
                      />
                      <Text ml={3} fontSize="sm" color="gray.600">
                        {newCompany.apiSecretIsRequired
                          ? "Required"
                          : "Optional"}
                      </Text>
                    </Flex>
                  </FormControl>
                </Grid>

                <Grid templateColumns="1fr 1fr" gap={6}>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">
                      <Flex align="center" gap={2}>
                        <FiKey size={14} />
                        {t("deliveryConfig.apiKeyLabel")}
                      </Flex>
                    </FormLabel>
                    <Input
                      value={newCompany.apiKeyLabel}
                      onChange={(e) =>
                        setNewCompany({
                          ...newCompany,
                          apiKeyLabel: e.target.value,
                        })
                      }
                      placeholder="e.g., API Key"
                      size="lg"
                      _hover={{ borderColor: "blue.400" }}
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182ce",
                      }}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontWeight="medium" fontSize="sm">
                      <Flex align="center" gap={2}>
                        <FiLock size={14} />
                        {t("deliveryConfig.apiSecretLabel")}
                      </Flex>
                    </FormLabel>
                    <Input
                      value={newCompany.apiSecretLabel}
                      onChange={(e) =>
                        setNewCompany({
                          ...newCompany,
                          apiSecretLabel: e.target.value,
                        })
                      }
                      placeholder="e.g., API Secret"
                      size="lg"
                      _hover={{ borderColor: "blue.400" }}
                      _focus={{
                        borderColor: "blue.500",
                        boxShadow: "0 0 0 1px #3182ce",
                      }}
                    />
                  </FormControl>
                </Grid>
              </VStack>
            </Box>

            {/* Fragments Section */}
            <Box
              bg={cardBg}
              p={6}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={borderColor}
              boxShadow="sm"
            >
              <Flex align="center" mb={6}>
                <FiDatabase size={20} style={{ marginRight: "12px" }} />
                <Text fontSize="lg" fontWeight="semibold">
                  {t("deliveryConfig.customFields") || "Custom Fields"}
                </Text>
                <Divider flex={1} ml={4} />
              </Flex>

              {onAdding ? (
                <Box
                  bg={sectionBg}
                  p={4}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={borderColor}
                >
                  <Text fontWeight="medium" mb={4}>
                    <Flex align="center" gap={2}>
                      <FiPlus />
                      {t("deliveryConfig.addNewField") || "Add New Field"}
                    </Flex>
                  </Text>
                  <Grid templateColumns="repeat(4, 1fr)" gap={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        {t("deliveryFragment.label")}
                      </FormLabel>
                      <Input
                        placeholder={t("deliveryFragment.enterLabel")}
                        isRequired
                        onChange={(e) =>
                          setFragment({ ...fragment, label: e.target.value })
                        }
                        size="md"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm">
                        {t("deliveryFragment.key")}
                      </FormLabel>
                      <Input
                        placeholder={t("deliveryFragment.enterKey")}
                        isRequired
                        onChange={(e) =>
                          setFragment({ ...fragment, key: e.target.value })
                        }
                        size="md"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm">
                        {t("deliveryConfig.type")}
                      </FormLabel>
                      <Select
                        placeholder={t("deliveryConfig.type")}
                        isRequired
                        onChange={(e) => [
                          setSelectedType(e.target.value),
                          setFragment({ ...fragment, type: e.target.value }),
                        ]}
                        size="md"
                      >
                        <option key="text" value="text">
                          {t("deliveryFragment.type.text")}
                        </option>
                        <option key="number" value="number">
                          {t("deliveryFragment.type.number")}
                        </option>
                        <option key="boolean" value="boolean">
                          {t("deliveryFragment.type.boolean")}
                        </option>
                        <option key="select" value="select">
                          {t("deliveryFragment.type.select")}
                        </option>
                      </Select>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm">
                        {t("deliveryFragment.defaultValue")}
                      </FormLabel>
                      {selectedType && selectedType === "select" ? (
                        <Select
                          placeholder={t("deliveryFragment.enterDefaultValue")}
                          isRequired
                          onChange={(e) =>
                            setFragment({
                              ...fragment,
                              defaultValue: e.target.value,
                            })
                          }
                          size="md"
                        >
                          <option value="governorate">
                            {t("deliveryFragment.governorate")}
                          </option>
                          <option value="city">
                            {t("deliveryFragment.city")}
                          </option>
                        </Select>
                      ) : selectedType === "boolean" ? (
                        <Select
                          placeholder={t("deliveryFragment.enterDefaultValue")}
                          isRequired
                          onChange={(e) =>
                            setFragment({
                              ...fragment,
                              defaultValue: e.target.value,
                            })
                          }
                          size="md"
                        >
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </Select>
                      ) : (
                        <Input
                          type={selectedType}
                          placeholder={t("deliveryFragment.enterDefaultValue")}
                          isRequired
                          onChange={(e) =>
                            setFragment({
                              ...fragment,
                              defaultValue: e.target.value,
                            })
                          }
                          size="md"
                        />
                      )}
                    </FormControl>
                  </Grid>

                  <Grid templateColumns="1fr 1fr auto" gap={4} mt={4}>
                    <FormControl>
                      <FormLabel fontSize="sm">
                        {t("deliveryFragment.isRequired")}
                      </FormLabel>
                      <Flex align="center" h="40px">
                        <Switch
                          onChange={(e) =>
                            setFragment({
                              ...fragment,
                              isRequired: e.target.checked,
                            })
                          }
                          colorScheme="red"
                        />
                        <Text ml={2} fontSize="sm" color="gray.600">
                          {fragment.isRequired ? "Required" : "Optional"}
                        </Text>
                      </Flex>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm">
                        {t("deliveryFragment.entryByCustomer")}
                      </FormLabel>
                      <Flex align="center" h="40px">
                        <Switch
                          onChange={(e) =>
                            setFragment({
                              ...fragment,
                              entryByCustomer: e.target.checked,
                            })
                          }
                          colorScheme="green"
                        />
                        <Text ml={2} fontSize="sm" color="gray.600">
                          {fragment.entryByCustomer ? "Yes" : "No"}
                        </Text>
                      </Flex>
                    </FormControl>

                    <FormControl>
                      <FormLabel fontSize="sm">
                        {t("deliveryFragment.save")}
                      </FormLabel>
                      <Flex align="center" h="40px" gap={2}>
                        <Button
                          aria-label="Save fragment"
                          onClick={handleAddingNewFragment}
                          colorScheme="blue"
                          size="md"
                          leftIcon={<IoMdCheckmark />}
                        >
                          {t("deliveryFragment.save")}
                        </Button>
                        <IconButton
                          aria-label="Cancel"
                          icon={<IoMdClose />}
                          onClick={() => {
                            setOnAdding(false);
                            setSelectedType(undefined);
                          }}
                          variant="ghost"
                          colorScheme="red"
                          size="md"
                        />
                      </Flex>
                    </FormControl>
                  </Grid>
                </Box>
              ) : (
                <Button
                  onClick={() => setOnAdding(true)}
                  size="md"
                  leftIcon={<FiPlus />}
                  colorScheme="blue"
                  variant="outline"
                  _hover={{
                    bg: "blue.50",
                    transform: "translateY(-2px)",
                    boxShadow: "sm",
                  }}
                  transition="all 0.2s"
                >
                  {t("deliveryConfig.addFragment")}
                </Button>
              )}

              <Box mt={6}>
                <Flex
                  align="center"
                  cursor="pointer"
                  onClick={() => setShowFragments(!showFragments)}
                  p={4}
                  borderRadius="md"
                  borderWidth="1px"
                  borderColor={showFragments ? "blue.300" : borderColor}
                  bg={showFragments ? "blue.50" : cardBg}
                  _hover={{
                    bg: showFragments ? "blue.100" : hoverBg,
                    borderColor: showFragments ? "blue.400" : "gray.300",
                  }}
                  transition="all 0.2s ease"
                  boxShadow={showFragments ? "sm" : "none"}
                  position="relative"
                >
                  <Box
                    transform={
                      showFragments ? "rotate(180deg)" : "rotate(0deg)"
                    }
                    transition="transform 0.3s ease"
                    mr={3}
                  >
                    <ChevronDownIcon
                      boxSize={6}
                      color={showFragments ? "blue.500" : "gray.500"}
                    />
                  </Box>

                  <VStack align="start" spacing={1} flex={1}>
                    <Text
                      fontWeight="semibold"
                      fontSize="md"
                      color={showFragments ? "blue.700" : "gray.700"}
                    >
                      {showFragments
                        ? t("deliveryFragment.hideFragments") ||
                          "Hide Custom Fields"
                        : t("deliveryFragment.showFragments") ||
                          "Show Custom Fields"}
                    </Text>
                    <Text
                      fontSize="sm"
                      color={showFragments ? "blue.600" : "gray.500"}
                    >
                      {fragments.length === 0
                        ? t("deliveryFragment.noFields") ||
                          "No fields configured yet"
                        : `${fragments.length} ${t("deliveryFragment.fieldsConfigured") || "field(s) configured"}`}
                    </Text>
                  </VStack>

                  {fragments.length > 0 && (
                    <Badge
                      colorScheme={showFragments ? "blue" : "gray"}
                      variant="solid"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="sm"
                      ml={2}
                    >
                      {fragments.length}
                    </Badge>
                  )}
                </Flex>

                <Collapse in={showFragments} animateOpacity>
                  <Box mt={4}>
                    <Fragments
                      fragments={fragments}
                      setFragments={setFragments}
                      handleDeleteFragment={handleDeleteFragment}
                    />
                  </Box>
                </Collapse>
              </Box>
            </Box>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
